import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ITEM_TYPES = [
  { value: 'part', label: 'Refacción' },
  { value: 'labor', label: 'Mano de Obra' },
  { value: 'service', label: 'Servicio' },
  { value: 'tire', label: 'Llanta' },
  { value: 'other', label: 'Otro' },
];

const emptyItem = () => ({
  id: Date.now() + Math.random(),
  type: 'part',
  description: '',
  quantity: 1,
  cost: '',
  price: '',
});

const DirectQuotationModal = ({ onClose, onConfirm, isProcessing = false, initialData = null }) => {
  const [clientData, setClientData] = useState({
    nombre: initialData?.clientNombre || '',
    telefono: initialData?.clientTelefono || '',
  });

  const [vehicleData, setVehicleData] = useState({
    marca: initialData?.vehicleMarca || '',
    modelo: initialData?.vehicleModelo || '',
    year: initialData?.vehicleYear || '',
    vin: initialData?.vin || '',
    placas: initialData?.vehiclePlacas || '',
  });

  const [observaciones, setObservaciones] = useState(initialData?.observaciones || '');
  const [items, setItems] = useState([emptyItem()]);
  const [errors, setErrors] = useState({});

  const handleClientChange = (field, value) => {
    setClientData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleVehicleChange = (field, value) => {
    setVehicleData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const handleItemChange = (id, field, value) => {
    setItems(prev =>
      prev?.map(item => (item?.id === id ? { ...item, [field]: value } : item))
    );
  };

  const handleAddItem = () => {
    setItems(prev => [...prev, emptyItem()]);
  };

  const handleRemoveItem = (id) => {
    setItems(prev => prev?.filter(item => item?.id !== id));
  };

  const validate = () => {
    const newErrors = {};
    if (!clientData?.nombre?.trim()) newErrors.nombre = 'Nombre del cliente requerido';
    if (!vehicleData?.marca?.trim()) newErrors.marca = 'Marca requerida';
    if (!vehicleData?.modelo?.trim()) newErrors.modelo = 'Modelo requerido';
    if (!vehicleData?.year?.trim()) newErrors.year = 'Año requerido';

    const validItems = items?.filter(i => i?.description?.trim());
    if (validItems?.length === 0) newErrors.items = 'Agrega al menos un ítem a la cotización';

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleConfirm = () => {
    if (!validate()) return;

    const builtItems = items?.filter(i => i?.description?.trim())?.map(i => ({
        ...i,
        id: Date.now() + Math.random(),
        cost: parseFloat(i?.cost) || 0,
        price: parseFloat(i?.price) || 0,
        quantity: parseInt(i?.quantity) || 1,
        margin:
          parseFloat(i?.cost) > 0
            ? (((parseFloat(i?.price) || 0) - parseFloat(i?.cost)) / parseFloat(i?.cost)) * 100
            : 0,
        addedAt: new Date()?.toISOString(),
        fromDirect: true,
      }));

    onConfirm({
      clientData,
      vehicleData,
      observaciones,
      items: builtItems,
      solicitudId: initialData?.solicitudId || null,
      clientId: initialData?.clientId || null,
      vehicleId: initialData?.vehicleId || null,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 p-4 overflow-y-auto">
      <div className="bg-card border border-border rounded-xl w-full max-w-2xl shadow-2xl my-6">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-primary/10">
              <Icon name="FilePlus" size={22} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-bold text-foreground">
                Nueva Cotización Directa
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                Sin inspección previa — origen: directa
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isProcessing}
            className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="px-6 py-5 space-y-6 max-h-[75vh] overflow-y-auto">
          {/* Cliente */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="User" size={16} className="text-primary" />
              <h3 className="text-sm font-heading font-bold text-foreground uppercase tracking-wide">
                Datos del Cliente
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Input
                label="Cliente"
                required
                value={clientData?.nombre}
                onChange={e => handleClientChange('nombre', e?.target?.value)}
                error={errors?.nombre}
                placeholder="Nombre completo"
              />
              <Input
                label="Teléfono"
                value={clientData?.telefono}
                onChange={e => handleClientChange('telefono', e?.target?.value)}
                placeholder="+52 55 1234 5678"
              />
            </div>
          </section>

          {/* Vehículo */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Icon name="Car" size={16} className="text-primary" />
              <h3 className="text-sm font-heading font-bold text-foreground uppercase tracking-wide">
                Datos del Vehículo
              </h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Input
                label="Marca"
                required
                value={vehicleData?.marca}
                onChange={e => handleVehicleChange('marca', e?.target?.value)}
                error={errors?.marca}
                placeholder="Ej: Toyota"
              />
              <Input
                label="Modelo"
                required
                value={vehicleData?.modelo}
                onChange={e => handleVehicleChange('modelo', e?.target?.value)}
                error={errors?.modelo}
                placeholder="Ej: Corolla"
              />
              <Input
                label="Año"
                required
                value={vehicleData?.year}
                onChange={e => handleVehicleChange('year', e?.target?.value)}
                error={errors?.year}
                placeholder="Ej: 2022"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              <Input
                label="VIN (opcional)"
                value={vehicleData?.vin}
                onChange={e => handleVehicleChange('vin', e?.target?.value)}
                placeholder="17 caracteres"
                className="font-mono"
              />
              <Input
                label="Placas (opcional)"
                value={vehicleData?.placas}
                onChange={e => handleVehicleChange('placas', e?.target?.value)}
                placeholder="Ej: ABC-123-D"
              />
            </div>
          </section>

          {/* Ítems */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Icon name="Package" size={16} className="text-primary" />
                <h3 className="text-sm font-heading font-bold text-foreground uppercase tracking-wide">
                  Refacciones y Mano de Obra
                </h3>
              </div>
              <button
                type="button"
                onClick={handleAddItem}
                className="flex items-center gap-1.5 text-xs font-medium text-primary hover:text-primary/80 transition-colors px-2 py-1 rounded-lg hover:bg-primary/10"
              >
                <Icon name="Plus" size={14} />
                Agregar ítem
              </button>
            </div>

            {errors?.items && (
              <p className="text-xs text-destructive mb-2">{errors?.items}</p>
            )}

            <div className="space-y-3">
              {items?.map((item, idx) => (
                <div
                  key={item?.id}
                  className="border border-border rounded-lg p-3 bg-muted/20 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-heading font-bold text-muted-foreground uppercase">
                      Ítem {idx + 1}
                    </span>
                    {items?.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item?.id)}
                        className="p-1 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                      >
                        <Icon name="Trash2" size={14} />
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <Select
                      label="Tipo"
                      options={ITEM_TYPES}
                      value={item?.type}
                      onChange={val => handleItemChange(item?.id, 'type', val)}
                    />
                    <div className="sm:col-span-2">
                      <Input
                        label="Descripción"
                        value={item?.description}
                        onChange={e => handleItemChange(item?.id, 'description', e?.target?.value)}
                        placeholder="Ej: Filtro de aceite, Mano de obra motor..."
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-3">
                    <Input
                      label="Cantidad"
                      type="number"
                      min="1"
                      value={item?.quantity}
                      onChange={e => handleItemChange(item?.id, 'quantity', e?.target?.value)}
                    />
                    <Input
                      label="Costo (MXN)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={item?.cost}
                      onChange={e => handleItemChange(item?.id, 'cost', e?.target?.value)}
                      placeholder="0.00"
                    />
                    <Input
                      label="Precio (MXN)"
                      type="number"
                      min="0"
                      step="0.01"
                      value={item?.price}
                      onChange={e => handleItemChange(item?.id, 'price', e?.target?.value)}
                      placeholder="0.00"
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Observaciones */}
          <section>
            <div className="flex items-center gap-2 mb-2">
              <Icon name="MessageSquare" size={16} className="text-primary" />
              <h3 className="text-sm font-heading font-bold text-foreground uppercase tracking-wide">
                Observaciones (opcional)
              </h3>
            </div>
            <textarea
              value={observaciones}
              onChange={e => setObservaciones(e?.target?.value)}
              rows={3}
              placeholder="Notas adicionales sobre la cotización..."
              className="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary resize-none"
            />
          </section>

          {/* Origin badge */}
          <div className="flex items-center gap-2 px-3 py-2 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="Info" size={16} className="text-warning flex-shrink-0" />
            <p className="text-xs text-warning font-medium">
              Esta cotización se guardará con origen <strong>"directa"</strong> — sin pasar por Inspección.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-border">
          <Button
            variant="default"
            iconName="FilePlus"
            iconPosition="left"
            onClick={handleConfirm}
            disabled={isProcessing}
            loading={isProcessing}
            fullWidth
          >
            Guardar Cotización Directa
          </Button>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={isProcessing}
            fullWidth
          >
            Cancelar
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DirectQuotationModal;
